import { Box, Icon, Tooltip, Typography } from "@outschool/backpack";
import * as BusinessRules from "@outschool/business-rules";
import {
  Activity,
  CurrencyCode,
  Enrollment,
  Section,
} from "@outschool/gql-backend-generated";
import { faQuestionCircle } from "@outschool/icons";
import {
  displayPerStudentPrice,
  displayPerStudentPriceCredits,
  renderCurrencyString,
  useTranslation,
} from "@outschool/localization";
import { Activity_PricingFragment } from "@outschool/localization/src/useLocalPriceDisplay/Pricing";
import { useSession } from "@outschool/ui-auth";
import React, { useCallback } from "react";

import { useEsaUserWithoutPaidMembership } from "../../hooks/useEsaUserWithoutPaidMembership";

type CreditBoxProps = {
  activity: Activity;
  section: Section;
  enrollment: Enrollment;
};

export const CreditBox = ({
  activity,
  section,
  enrollment,
}: CreditBoxProps) => {
  const { currentUser } = useSession();
  const canPayWithSubscriptionCredits =
    currentUser?.canPayWithSubscriptionCredits;
  const { isEsaUserWithoutPaidMembership } = useEsaUserWithoutPaidMembership();

  const isRecurringFormat =
    activity.isClub ||
    activity.is_ongoing_weekly ||
    BusinessRules.isOneOnOneFormat(activity);

  const serviceFeePercentage =
    (isRecurringFormat
      ? enrollment.service_fee_percentage
      : enrollment.payments[0]?.service_fee_percentage) ?? 0;

  const convertWithServiceFee = useCallback(
    (priceCents: number) =>
      BusinessRules.applyServiceFee(priceCents, serviceFeePercentage),
    [serviceFeePercentage],
  );

  const enrollmentPrice = BusinessRules.perStudentPriceInCents(
    activity,
    section,
    enrollment,
  );

  const totalPrice = BusinessRules.applyServiceFee(
    enrollmentPrice,
    serviceFeePercentage,
  );

  return (
    <Box flex sx={{ marginRight: "1rem" }}>
      <Typography variant="subtitle2">
        {canPayWithSubscriptionCredits
          ? displayPerStudentPriceCredits(
              activity as Activity_PricingFragment,
              section,
              enrollment,
              isEsaUserWithoutPaidMembership,
            )
          : displayPerStudentPrice(
              activity,
              section,
              enrollment,
              undefined,
              serviceFeePercentage ? convertWithServiceFee : undefined,
            )}
      </Typography>
      {!!serviceFeePercentage && !canPayWithSubscriptionCredits && (
        <PriceBreakdownWithServiceFee
          enrollmentPrice={enrollmentPrice}
          totalPrice={totalPrice}
          currencyCode={enrollment.currency_code}
        />
      )}
    </Box>
  );
};

const PriceBreakdownWithServiceFee = ({
  enrollmentPrice,
  totalPrice,
  currencyCode,
}: {
  enrollmentPrice: number;
  totalPrice: number;
  currencyCode: CurrencyCode;
}) => {
  const { t } = useTranslation("client\\routes\\UpcomingParentSchedule");

  return (
    <Tooltip
      title={
        <Box flex sx={{ flexDirection: "column" }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: "medium", marginBottom: "1rem" }}
          >
            {t("Total cost: {{totalPriceString}}", {
              totalPriceString: renderCurrencyString({
                priceInCents: totalPrice,
                showCurrencyCode: true,
                currencyCode,
              }),
            })}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {t("Includes:")}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: "medium" }}>
            {t("{{enrollmentPriceString}} Class Fee", {
              enrollmentPriceString: renderCurrencyString({
                priceInCents: enrollmentPrice,
                showCurrencyCode: true,
                currencyCode,
              }),
            })}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: "medium" }}>
            {t("{{serviceFeePriceString}} Service Fee", {
              serviceFeePriceString: renderCurrencyString({
                priceInCents: totalPrice - enrollmentPrice,
                showCurrencyCode: true,
                currencyCode,
              }),
            })}
          </Typography>
        </Box>
      }
    >
      <Icon
        icon={faQuestionCircle}
        color="primary"
        sx={{ marginLeft: "0.25em", marginRight: "0.25em" }}
      />
    </Tooltip>
  );
};
